import { useCallback, useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReliabilityBadge, ConfidenceBar, Spinner } from "@/components/bits";
import { toast } from "sonner";
import { Eye, Plus, Trash2, RadarIcon, ScanLine, CalendarClock, Play, FileText } from "lucide-react";

export default function Watchlist() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("");
  const [scanning, setScanning] = useState(null);
  const [proposing, setProposing] = useState(null);
  const [settings, setSettings] = useState({ enabled: true, interval_hours: 24, last_run: null });
  const [scanningAll, setScanningAll] = useState(false);
  const [progress, setProgress] = useState(null);
  const canManage = user?.permissions?.manage;

  const load = useCallback(() => client.get("/watchlist").then((r) => setItems(r.data)), []);
  const loadSettings = useCallback(() => client.get("/watchlist-settings").then((r) => setSettings(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadSettings(); }, [load, loadSettings]);

  useEffect(() => {
    if (!scanningAll) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await client.get("/watchlist/scan-status");
        setProgress({ done: data.done, total: data.total });
        if (!data.running) {
          clearInterval(iv);
          setScanningAll(false);
          setProgress(null);
          await load(); await loadSettings();
          toast.success(`Scan complete — ${data.total} item(s) refreshed. Findings only, nothing published.`);
        }
      } catch (e) { console.error("watchlist action failed", e); toast.error(e?.response?.data?.detail || "Action failed"); }
    }, 3000);
    return () => clearInterval(iv);
  }, [scanningAll, load, loadSettings]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await client.post("/watchlist", { label, query, official_source: source, auto_scan: true });
      setLabel(""); setQuery(""); setSource("");
      load();
      toast.success("Watchlist item added");
    } catch (ex) { toast.error(apiErr(ex)); }
  };

  const remove = async (id) => { await client.delete(`/watchlist/${id}`); load(); };

  const scan = async (id) => {
    setScanning(id);
    try {
      await client.post(`/watchlist/${id}/scan`);
      await load();
      toast.success("Scan complete — findings only, nothing published");
    } catch (ex) { toast.error(apiErr(ex)); } finally { setScanning(null); }
  };

  const createProposal = async (id) => {
    setProposing(id);
    try {
      const { data } = await client.post(`/watchlist/${id}/create-proposal`);
      const jobId = data.job_id;
      const started = Date.now();
      const poll = async () => {
        try {
          const { data: job } = await client.get(`/jobs/${jobId}`);
          if (job.status === "done") {
            const r = job.result || {};
            if (r.proposal_status === "NO_CHANGE") toast.info("No verified change detected — nothing to propose right now.");
            else toast.success(`Draft proposal created (${r.proposal_status}) for “${r.candidate?.title || "post"}”. Open Change Review.`);
            setProposing(null);
            return;
          }
          if (job.status === "error") { toast.error(job.error || "Draft proposal failed"); setProposing(null); return; }
          if (Date.now() - started > 180000) { toast.error("Draft proposal is taking too long — check Change Review shortly."); setProposing(null); return; }
          setTimeout(poll, 2500);
        } catch (e) { toast.error(apiErr(e)); setProposing(null); }
      };
      setTimeout(poll, 2500);
    } catch (ex) { toast.error(apiErr(ex)); setProposing(null); }
  };

  const runAll = async () => {
    try {
      const { data } = await client.post("/watchlist/scan-all");
      if (!data.started || data.queued === 0) {
        toast.info("No watchlist items to scan. Add one first.");
        return;
      }
      setScanningAll(true);
      setProgress({ done: 0, total: data.queued });
      toast.success(`Scanning ${data.queued} item(s) in the background — nothing is published.`);
    } catch (ex) { toast.error(apiErr(ex)); }
  };

  const saveSettings = async (next) => {
    try {
      const { data } = await client.post("/watchlist-settings", {
        enabled: next.enabled, interval_hours: next.interval_hours, min_confidence: next.min_confidence ?? 0,
      });
      setSettings((s) => ({ ...s, ...data }));
      toast.success(`Saved · auto-scan ${data.enabled ? "on" : "off"} · min confidence ${data.min_confidence}%`);
    } catch (ex) { toast.error(apiErr(ex)); }
  };

  const toggleItemAuto = async (it) => {
    try {
      await client.patch(`/watchlist/${it.id}`, { auto_scan: !(it.auto_scan !== false) });
      load();
    } catch (ex) { toast.error(apiErr(ex)); }
  };

  return (
    <div className="max-w-5xl">
      <p className="text-xs font-mono uppercase tracking-widest text-blue-700">Findings Only · Never Auto-Publish</p>
      <h1 className="font-serif text-3xl font-bold text-slate-900 mt-1 mb-6">Watchlist</h1>

      {/* Auto-scan control bar */}
      <div data-testid="auto-scan-settings-bar" className="rounded-xl border border-slate-200 bg-white p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <CalendarClock className="h-5 w-5 text-blue-600" />
          <div>
            <div className="text-sm font-semibold text-slate-800">
              Daily Auto-Scan{" "}
              <span className={`ml-1 rounded-full px-2 py-0.5 text-[11px] font-mono ${settings.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {settings.enabled ? `EVERY ${settings.interval_hours}H` : "OFF"}
              </span>
            </div>
            <div className="text-xs text-slate-400">
              Files findings automatically — never publishes. {settings.last_run ? `Last run: ${new Date(settings.last_run).toLocaleString()}` : "Not run yet"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="run-all-scans-btn" onClick={runAll} disabled={scanningAll} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
            <Play className="h-3.5 w-3.5" /> {scanningAll ? `Scanning ${progress?.done ?? 0}/${progress?.total ?? "?"}…` : "Run All Scans Now"}
          </button>
          {canManage && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Min confidence</label>
              <input data-testid="min-confidence-input" type="number" min="0" max="100" value={settings.min_confidence ?? 0}
                onChange={(e) => setSettings((s) => ({ ...s, min_confidence: Number(e.target.value) }))}
                onBlur={() => saveSettings({ ...settings })}
                className="w-16 rounded-lg border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-blue-500" />
              <span className="text-xs text-slate-400">%</span>
            </div>
          )}
          {canManage && (
            <button data-testid="toggle-auto-scan-btn" onClick={() => saveSettings({ ...settings, enabled: !settings.enabled })} className={`rounded-lg border px-3 py-2 text-xs font-semibold ${settings.enabled ? "border-slate-300 text-slate-600 hover:border-rose-400 hover:text-rose-600" : "border-emerald-400 text-emerald-700 hover:bg-emerald-50"}`}>
              {settings.enabled ? "Disable Auto-Scan" : "Enable Auto-Scan"}
            </button>
          )}
        </div>
      </div>

      {scanningAll && progress && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center justify-between text-xs text-blue-800 mb-1.5">
            <span>Background scan in progress — findings only</span>
            <span className="font-mono">{progress.done}/{progress.total || "?"}</span>
          </div>
          <div className="h-2 w-full rounded-full bg-blue-100 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 10}%` }} />
          </div>
        </div>
      )}

      {canManage && (
        <form onSubmit={add} className="rounded-xl border border-slate-200 bg-white p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input data-testid="watchlist-label-input" value={label} onChange={(e) => setLabel(e.target.value)} required placeholder="Label (e.g. JEE Main)" className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <input data-testid="watchlist-query-input" value={query} onChange={(e) => setQuery(e.target.value)} required placeholder="Research query" className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Official source URL (optional)" className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <button data-testid="add-watchlist-item-btn" className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>
        </form>
      )}

      {(() => {
        const dr = items.filter((i) => i.origin === "deep_research");
        if (!dr.length) return null;
        const groups = {};
        dr.forEach((i) => { const t = (i.label || "").split(" — ")[0] || ("Post " + i.post_id); (groups[t] = groups[t] || []).push(i); });
        return (
          <div data-testid="deep-research-findings" className="mb-6">
            <h2 className="font-serif text-lg font-bold text-slate-900 mb-2">Deep Research Findings</h2>
            <div className="space-y-3">
              {Object.entries(groups).map(([title, arr]) => (
                <div key={title} className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-800 truncate">{title}</span>
                    <span className="text-xs font-mono text-indigo-600">{arr.length} finding(s)</span>
                  </div>
                  <div className="space-y-2">
                    {arr.map((it) => { const f = (it.findings || [])[0] || {}; return (
                      <div key={it.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-700 truncate">{it.field}</div>
                          <div className="text-xs text-slate-500 truncate">→ {f.proposed_value || "—"}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {f.confidence != null && <span className="text-[11px] font-mono text-slate-500">{f.confidence}%</span>}
                          <button onClick={() => scan(it.id)} disabled={scanning === it.id} className="rounded-lg border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-blue-500 disabled:opacity-50">{scanning === it.id ? "…" : "Re-scan"}</button>
                        </div>
                      </div>
                    ); })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div data-testid="watchlist-crud-table" className="space-y-3">
        {items.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-500">No watchlist items yet.</div>}
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-slate-500" />
                <span className="font-semibold text-slate-800">{it.label}</span>
                <span className="font-mono text-xs text-slate-400">{it.query}</span>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="run-watchlist-scan-btn" onClick={() => scan(it.id)} disabled={scanning === it.id} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-blue-500 disabled:opacity-50">
                  <ScanLine className="h-3.5 w-3.5" /> {scanning === it.id ? "Scanning…" : "Run Scan"}
                </button>
                <button data-testid="create-draft-proposal-btn" onClick={() => createProposal(it.id)} disabled={proposing === it.id} className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:border-indigo-500 disabled:opacity-50">
                  <FileText className="h-3.5 w-3.5" /> {proposing === it.id ? "Drafting…" : "Create Draft Proposal"}
                </button>
                {canManage && (
                  <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none" data-testid="item-auto-scan-toggle">
                    <input type="checkbox" checked={it.auto_scan !== false} onChange={() => toggleItemAuto(it)} className="rounded border-slate-300" /> auto
                  </label>
                )}
                {canManage && <button onClick={() => remove(it.id)} className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:text-rose-600 hover:border-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
            </div>
            {it.last_scan && <div className="text-xs text-slate-400 mt-2">Last scan: {new Date(it.last_scan).toLocaleString()}</div>}
            {it.findings?.length > 0 && (
              <div className="mt-3 space-y-2">
                {it.findings.map((f, i) => (
                  <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-700">{f.fact}</span>
                      <ReliabilityBadge level={f.reliability} />
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Value: <span className="font-mono">{f.proposed_value}</span></div>
                    <div className="mt-1"><ConfidenceBar value={f.confidence} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
